import { useQuery } from '@tanstack/react-query'
import { getProjectHistory } from '../transport/api'
import { ProjectFiles, collectFiles } from '../chat/ProjectFiles'

/**
 * THE PROJECT HALF OF THE FILES DOCK — owner, 2026-08-23: Project Chat 的 File Browser
 * 無法打開 (機制與 Agent Session 不同).
 *
 * He is right that it is a different mechanism, and that is exactly why the dock had to
 * grow a second branch rather than one component being taught about both: an agent's
 * browser walks a MACHINE's filesystem over the relay, while a project's files are the
 * ones shared into the conversation, collected out of its own messages.
 *
 * This exists to do the collecting the panel normally does, so `ProjectFiles` gets the
 * `files` prop it has always taken. Same query key as the panel's, so opening the dock
 * on a project already on screen asks nothing new.
 */
/** How far back the Files dock looks. Deep enough that a project's files are actually
 *  its files; bounded because a history can be arbitrarily long. */
const DOCK_FILE_SCAN = 500

export function ProjectDockFiles({
  projectId,
  selfEmail,
  onOpenDoc,
  onClose,
}: {
  projectId: string
  selfEmail: string | null
  onOpenDoc: (a: { url: string; name: string; mime: string }) => void
  onClose: () => void
}) {
  /*
   * ITS OWN KEY, AND ITS OWN DEPTH — both deliberate, and the first one is a bug fix.
   *
   * This shared `['project-history', projectId]` with ProjectImage. react-query keys a
   * cache entry, not a fetcher: whichever component mounts first registers its queryFn
   * for that key and the other silently receives ITS result. The two happened to ask for
   * the same thing, so nothing was wrong — but the moment either wanted a different
   * depth, and this one does, the other would have started reading a page it never
   * asked for. Two readers, one key, different needs is the shape that only shows up
   * once someone edits the innocent-looking half.
   *
   * The depth: this dock is titled Files and its job is to list them, so listing "the
   * files mentioned in the newest 50 messages" was a quiet falsehood that got worse the
   * longer a project stayed alive. It is opened deliberately, one query, not on every
   * inline notice — so it can afford to look much further back than the panel does.
   * ProjectImage takes the opposite trade (newest page, then a bounded walk on a miss)
   * because it runs per notice.
   */
  const hist = useQuery({
    queryKey: ['project-file-list', projectId],
    queryFn: () => getProjectHistory(projectId, undefined, DOCK_FILE_SCAN),
    staleTime: 30_000,
  })
  const files = collectFiles(hist.data?.messages ?? [], selfEmail)
  return (
    <ProjectFiles
      projectId={projectId}
      files={files}
      onClose={onClose}
      onOpenDoc={onOpenDoc}
      /* Re-ask rather than mutate a list we do not own: the panel owns the
         conversation, and a delete here has to reach it the same way anything else
         that changed the history would. */
      onDeleted={() => void hist.refetch()}
    />
  )
}
